<template>
    <VJoyDialog
        ref="dialogRef"
        :confirm-text="t('profile.section.experience.associated.portfolio.items.dialog.close')"
        class="experience-associated-portfolio-items-modal"
        data-testid="experience-associated-portfolio-items-modal"
        size="large"
        @dialog:confirm="hide"
    >
        <template #dialog-header>
            {{ t('profile.section.experience.associated.portfolio.items.dialog.title') }}
        </template>
        <template #dialog-body>
            <VJoyWrapper column-gap="4" no-margin row-gap="4" wrap="wrap">
                <PortfolioItemCard
                    v-for="item in items"
                    :key="item.id"
                    :portfolio-item="item"
                    class="experience-associated-portfolio-items-modal__item"
                    @click="onClickItem(item)"
                />
            </VJoyWrapper>
        </template>
    </VJoyDialog>
</template>

<script lang="ts" setup>
    import {computed, useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {navigateTo, useTranslation} from '#imports';
    import {VJoyDialog, VJoyWrapper} from '@maltjoy/core-vue';
    import type {PortfolioItemSummaryResource} from '@api';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {anchorsPageProfile} from '~/config/anchors';
    import {usePortfolioItemsStore} from '~/features/portfolio/section/store/portfolio-items.store';
    import PortfolioItemCard from '~/features/portfolio/section/ui/PortfolioItemCard.vue';

    interface Props {
        portfolioItemIds: string[];
    }

    const {portfolioItemIds} = defineProps<Props>();
    const {t} = useTranslation();
    const {trackPortfolioItemClicked} = useProfileTracking();

    const dialogRef = useTemplateRef<InstanceType<typeof VJoyDialog>>('dialogRef');
    const {portfolioItems} = storeToRefs(usePortfolioItemsStore());

    const items = computed(() => portfolioItems.value.filter((item) => portfolioItemIds.includes(item.id)));

    async function onClickItem(item: PortfolioItemSummaryResource) {
        trackPortfolioItemClicked(item.id, 'experience_modal');
        await hide();
        await navigateTo({
            path: `/profile/${item.profileId}`,
            hash: `#${anchorsPageProfile.portfolioItemById(item.id)}`,
        });
    }

    function show() {
        dialogRef.value?.show();
    }

    function hide() {
        return dialogRef.value?.hide();
    }

    defineExpose({show});
</script>

<style lang="scss" scoped>
    .experience-associated-portfolio-items-modal {
        &__item {
            width: 304px;
        }
    }
</style>
